import { calculateCunningStrikeDice, getCunningStrikeMaxEffects, getCunningStrikeOptions } from './cunningStrike';

export const SNEAK_ATTACK_FEATURE_ID = "rogue:sneak-attack";

/** Sneak Attack gains one d6 at level 1 and at every odd Rogue level. */
export const getSneakAttackDiceCount = (rogueLevel) => {
  const level = Math.floor(Number(rogueLevel));
  return Number.isFinite(level) && level > 0 ? Math.ceil(level / 2) : 0;
};

const metadata = (rogueLevel, reasonCode) => ({
  featureId: SNEAK_ATTACK_FEATURE_ID,
  featureLabel: "Sneak Attack",
  rogueLevel,
  sneakAttackDice: { count: getSneakAttackDiceCount(rogueLevel), sides: 6 },
  reasonCode,
});

const ineligible = (rogueLevel, reasonCode) => ({
  status: "ineligible",
  ...metadata(rogueLevel, reasonCode),
});

const usageClaim = (context) => ({
  campaignId: context.combat.campaignId,
  combatantId: context.attacker.combatantId,
  featureId: SNEAK_ATTACK_FEATURE_ID,
  turnSequence: context.combat.turnSequence,
  resolutionId: context.resolutionId,
});

const damageComponents = (rogueLevel, count = getSneakAttackDiceCount(rogueLevel)) => [{
  id: SNEAK_ATTACK_FEATURE_ID,
  label: "Sneak Attack",
  source: { type: "feature", featureId: SNEAK_ATTACK_FEATURE_ID },
  dice: { count, sides: 6 },
  flat: 0,
  damageType: { mode: "inherit" },
  critical: { dice: "double" },
}];

const buildUseDecision = (rogueLevel, context) => {
  const baseDice = getSneakAttackDiceCount(rogueLevel);
  const improved = getCunningStrikeMaxEffects(rogueLevel) >= 2;
  const options = getCunningStrikeOptions({ rogueLevel, character: context.attacker?.entity, target: context.target?.entity, combatState: context.combatState, targetId: context.target?.combatantId, attackerId: context.attacker?.combatantId, resolutionId: context.resolutionId, baseDice });
  return ({
  id: `${context.resolutionId}:${SNEAK_ATTACK_FEATURE_ID}`,
  kind: "use-feature",
  featureId: SNEAK_ATTACK_FEATURE_ID,
  resolutionId: context.resolutionId,
  title: "Sneak Attack",
  label: "Sneak Attack",
  prompt: improved ? "Choose full Sneak Attack damage, or up to two different Cunning Strike effects (each costs Sneak Attack dice)."
    : rogueLevel >= 5 ? "Choose Sneak Attack damage or one Cunning Strike effect." : "Use Sneak Attack on this attack?",
  choices: [
    { id: "apply", action: 'apply', label: rogueLevel >= 5 ? `Full Sneak Attack · ${baseDice}d6` : "Use Sneak Attack" },
    ...(improved ? [] : options.map((effect) => ({ id: `cunning-strike:${effect.id}`, action: 'apply', label: `${effect.label} · ${effect.remainingDice}d6 + ${effect.label}`, disabled: effect.disabled, detail: effect.disabledReason }))),
    { id: "skip", action: 'skip', label: "Skip Sneak Attack" },
  ],
  // Improved Cunning Strike: pick up to two effects; the modal answers `cunning-strike:<id>+<id>`.
  ...(improved ? { selection: { prefix: 'cunning-strike:', max: getCunningStrikeMaxEffects(rogueLevel), budget: baseDice, unit: 'd6',
    confirmLabel: 'Use Cunning Strike', options: options.map((effect) => ({ id: effect.id, label: effect.label, cost: effect.dieCost,
      disabled: effect.disabled, detail: effect.disabledReason })) } } : {}),
  });
};

const resolveSelection = (evaluation, choiceId) => {
  const effectIds = String(choiceId).startsWith('cunning-strike:') ? String(choiceId).slice('cunning-strike:'.length).split('+') : [];
  const selectedEffects = effectIds.map((effectId) => {
    const option = evaluation.cunningStrike?.availableEffects.find((effect) => effect.id === effectId);
    if (!option || option.disabled) throw new Error(option?.disabledReason || 'Cunning Strike effect is unavailable.');
    return { id: option.id, label: option.label, dieCost: option.dieCost, saveAbility: option.saveAbility };
  });
  if (new Set(effectIds).size !== effectIds.length) throw new Error('Choose different Cunning Strike effects.');
  const arithmetic = calculateCunningStrikeDice({ baseDice: evaluation.sneakAttackDice.count, selectedEffects, maxEffects: evaluation.cunningStrike?.maxEffects || 0 });
  return { ...evaluation, components: damageComponents(evaluation.rogueLevel, arithmetic.remainingDice), sneakAttack: { ...arithmetic }, cunningStrike: { ...evaluation.cunningStrike, selectedEffects } };
};

// "automatic" describes rules eligibility, not the player's decision to spend
// this optional, once-per-turn feature. The decision remains feature-owned data.
const automatic = (rogueLevel, reasonCode, context) => ({
  status: "automatic",
  ...metadata(rogueLevel, reasonCode),
  components: damageComponents(rogueLevel),
  claims: [usageClaim(context)],
  decision: buildUseDecision(rogueLevel, context),
  cunningStrike: { maxEffects: getCunningStrikeMaxEffects(rogueLevel), availableEffects: getCunningStrikeOptions({ rogueLevel, character: context.attacker?.entity, target: context.target?.entity, combatState: context.combatState, targetId: context.target?.combatantId, attackerId: context.attacker?.combatantId, resolutionId: context.resolutionId, baseDice: getSneakAttackDiceCount(rogueLevel) }), selectedEffects: [] },
  resolveDecision(choiceId) { return resolveSelection(this, choiceId); },
});

const confirmationRequired = (rogueLevel, context) => ({
  status: "confirmation-required",
  ...metadata(rogueLevel, "nearby-enemy-of-target-unresolved"),
  components: damageComponents(rogueLevel),
  claims: [usageClaim(context)],
  decision: rogueLevel >= 5 ? {
    ...buildUseDecision(rogueLevel, context),
    kind: 'confirm-feature',
    prompt: 'Does this attack qualify for a Sneak Attack? If so, choose its damage or one Cunning Strike effect.',
    reasonCode: 'nearby-enemy-of-target-unresolved',
  } : {
    id: `${context.resolutionId}:${SNEAK_ATTACK_FEATURE_ID}`,
    kind: "confirm-feature",
    featureId: SNEAK_ATTACK_FEATURE_ID,
    resolutionId: context.resolutionId,
    title: "Sneak Attack",
    label: "Sneak Attack",
    prompt: "Does this attack qualify for a Sneak Attack?",
    reasonCode: "nearby-enemy-of-target-unresolved",
    choices: [
      { id: "apply", label: "Qualifies" },
      { id: "skip", label: "Does not qualify" },
    ],
  },
  cunningStrike: { maxEffects: getCunningStrikeMaxEffects(rogueLevel), availableEffects: getCunningStrikeOptions({ rogueLevel, character: context.attacker?.entity, target: context.target?.entity, combatState: context.combatState, targetId: context.target?.combatantId, attackerId: context.attacker?.combatantId, resolutionId: context.resolutionId, baseDice: getSneakAttackDiceCount(rogueLevel) }), selectedEffects: [] },
  resolveDecision(choiceId) { return resolveSelection(this, choiceId); },
});

/**
 * Evaluate rules qualification and describe generic usage and damage.
 * `hasQualifyingNearbyEnemy(context)` is a narrow tri-state query: true, false,
 * or "unknown". Missing/invalid answers are unknown, never false.
 */
export const evaluateSneakAttack = ({ rogueLevel, context, queries = {} }) => {
  if (!(rogueLevel > 0)) return ineligible(rogueLevel, "no-rogue-level");
  if (context?.roll?.hit !== true) return ineligible(rogueLevel, "not-a-hit");
  if (context?.attack?.kind !== "weapon" || context.attack.isWeaponAttack !== true) {
    return ineligible(rogueLevel, "not-weapon-attack");
  }
  if (!context.attack.isFinesse && !context.attack.isRanged) {
    return ineligible(rogueLevel, "weapon-not-finesse-or-ranged");
  }
  if (!context?.combat?.campaignId || !Number.isInteger(context?.combat?.turnSequence) || !context?.combat?.activeCombatantId ||
      !context?.combat?.participantIds?.includes(context?.attacker?.combatantId)) {
    return ineligible(rogueLevel, "no-authoritative-turn");
  }

  const previousUsage = context.combat.turnFeatureUsage?.[context.attacker.combatantId]?.[SNEAK_ATTACK_FEATURE_ID];
  // Preserve replay of the original attack; suppress prompts for a different
  // attack after a committed use on this turn. The server still guards races.
  if (previousUsage?.turnSequence === context.combat.turnSequence &&
      previousUsage.resolutionId !== context.resolutionId) {
    return ineligible(rogueLevel, "already-used-this-turn");
  }

  // A surviving disadvantage source is disqualifying. Opposing sources cancel,
  // so their final normal mode is neither Advantage nor Disadvantage.
  if (context.roll.mode === "disadvantage") return ineligible(rogueLevel, "disadvantage");
  if (context.roll.mode === "advantage") return automatic(rogueLevel, "advantage", context);

  const query = queries?.hasQualifyingNearbyEnemy;
  const nearby = typeof query === "function" ? query(context) : "unknown";
  if (nearby === true) return automatic(rogueLevel, "qualifying-nearby-enemy", context);
  if (nearby === false) return ineligible(rogueLevel, "no-qualifying-nearby-enemy");
  return confirmationRequired(rogueLevel, context);
};
